import { useEffect, useMemo, useState } from 'react';
import { Activity, AlertTriangle, CheckCircle2, Mail } from 'lucide-react';

import { EmailRegionalBreakdownPanel } from '@/components/dashboard';
import { Badge } from '@/components/ui/badge';
import { useI18n } from '@/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  DashboardPeriod,
  EmailDashboardOverviewResponse,
  useEmailRegionalDashboard,
  useEmailSLOOverview,
} from '@/hooks/use-email-dashboard';
import { useTenant } from '@/contexts/TenantContext';

const PERIOD_OPTIONS: DashboardPeriod[] = ['24h', '7d', '30d'];

interface ComplianceThresholds {
  healthyBounceMax: number;
  healthyComplaintMax: number;
  monitorBounceMax: number;
  monitorComplaintMax: number;
}

const DEFAULT_COMPLIANCE_THRESHOLDS: ComplianceThresholds = {
  healthyBounceMax: 2,
  healthyComplaintMax: 0.1,
  monitorBounceMax: 3,
  monitorComplaintMax: 0.2,
};

// ---------------------------------------------------------------------------
// SLO Overview
// ---------------------------------------------------------------------------

function statusVariant(
  status: EmailDashboardOverviewResponse['global_status'],
): 'default' | 'secondary' | 'destructive' {
  if (status === 'HEALTHY') return 'default';
  if (status === 'MONITOR') return 'secondary';
  return 'destructive';
}

function StatusIcon({
  status,
}: {
  status: EmailDashboardOverviewResponse['global_status'];
}) {
  if (status === 'HEALTHY') return <CheckCircle2 className="h-5 w-5 text-green-500" />;
  if (status === 'MONITOR') return <Activity className="h-5 w-5 text-yellow-500" />;
  return <AlertTriangle className="h-5 w-5 text-red-500" />;
}

function OverviewCards() {
  const { data, isLoading, error } = useEmailSLOOverview();
  const { t } = useI18n();

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28 rounded-lg" />
        ))}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {t('admin.emailDashboard.overview.loadError')}
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {/* Global SLO */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t('admin.emailDashboard.overview.globalSlo')}
          </CardTitle>
          <StatusIcon status={data.global_status} />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{data.global_slo.toFixed(2)}%</div>
          <Badge className="mt-1" variant={statusVariant(data.global_status)}>
            {data.global_status}
          </Badge>
        </CardContent>
      </Card>

      {/* Active regions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t('admin.emailDashboard.overview.activeRegions')}
          </CardTitle>
          <Mail className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{data.regions.length}</div>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('admin.emailDashboard.overview.reportingRegions')}
          </p>
        </CardContent>
      </Card>

      {/* Healthy regions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t('admin.emailDashboard.overview.healthyRegions')}
          </CardTitle>
          <CheckCircle2 className="h-5 w-5 text-green-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">
            {data.regions.filter((r) => r.status === 'HEALTHY').length}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('admin.emailDashboard.overview.ofRegions', { count: data.regions.length })}
          </p>
        </CardContent>
      </Card>

      {/* Open alerts */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            {t('admin.emailDashboard.overview.openAlerts')}
          </CardTitle>
          <AlertTriangle className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{data.alerts_count}</div>
          <p className="mt-1 text-xs text-muted-foreground">
            {t('admin.emailDashboard.overview.activeAlerts')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function OverviewSummaryPanel() {
  const { t } = useI18n();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.emailDashboard.overview.summaryTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          {t('admin.emailDashboard.overview.summaryText')}
        </p>
      </CardContent>
    </Card>
  );
}

function ProvidersPanel({ period }: { period: DashboardPeriod }) {
  const { data, isLoading, error } = useEmailRegionalDashboard(period);
  const { t } = useI18n();

  const providers = useMemo(() => {
    const map = new Map<string, number>();

    for (const metric of data?.metrics ?? []) {
      for (const [provider, count] of Object.entries(metric.provider_breakdown)) {
        map.set(provider, (map.get(provider) ?? 0) + count);
      }
    }

    const rows = Array.from(map.entries())
      .map(([provider, emails]) => ({ provider, emails }))
      .sort((a, b) => b.emails - a.emails);

    const totalEmails = rows.reduce((sum, row) => sum + row.emails, 0);

    return rows.map((row) => ({
      ...row,
      share: totalEmails > 0 ? Number(((row.emails / totalEmails) * 100).toFixed(2)) : 0,
    }));
  }, [data?.metrics]);

  if (isLoading) {
    return <Skeleton className="h-48 w-full rounded-lg" />;
  }

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {t('admin.emailDashboard.providers.loadError')}
      </div>
    );
  }

  if (providers.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('admin.emailDashboard.providers.title', { period })}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t('admin.emailDashboard.providers.noData')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.emailDashboard.providers.title', { period })}</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full min-w-[420px]">
          <thead>
            <tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 font-medium">{t('admin.emailDashboard.providers.colProvider')}</th>
              <th className="py-2 text-right font-medium">{t('admin.emailDashboard.providers.colEmails')}</th>
              <th className="py-2 text-right font-medium">{t('admin.emailDashboard.providers.colShare')}</th>
            </tr>
          </thead>
          <tbody>
            {providers.map((row) => (
              <tr key={row.provider} className="border-b last:border-0">
                <td className="py-3 text-sm font-medium">{row.provider}</td>
                <td className="py-3 text-right text-sm">{row.emails.toLocaleString()}</td>
                <td className="py-3 text-right text-sm">{row.share.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function AlertsPanel({ period }: { period: DashboardPeriod }) {
  const { data, isLoading, error } = useEmailRegionalDashboard(period);
  const { t } = useI18n();

  const alertRows = useMemo(() => {
    return (data?.metrics ?? [])
      .map((metric) => ({
        region: metric.region,
        alerts: metric.alerts.length,
        bounce: metric.bounce_rate,
        complaint: metric.complaint_rate,
      }))
      .sort((a, b) => b.alerts - a.alerts);
  }, [data?.metrics]);

  const totalAlerts = alertRows.reduce((sum, row) => sum + row.alerts, 0);

  if (isLoading) {
    return <Skeleton className="h-48 w-full rounded-lg" />;
  }

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {t('admin.emailDashboard.alerts.loadError')}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.emailDashboard.alerts.title', { period })}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('admin.emailDashboard.alerts.totalAlerts', { count: totalAlerts })}</p>

        {alertRows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('admin.emailDashboard.alerts.noActivity')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px]">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 font-medium">{t('admin.emailDashboard.alerts.colRegion')}</th>
                  <th className="py-2 text-right font-medium">{t('admin.emailDashboard.alerts.colAlerts')}</th>
                  <th className="py-2 text-right font-medium">{t('admin.emailDashboard.alerts.colBounce')}</th>
                  <th className="py-2 text-right font-medium">{t('admin.emailDashboard.alerts.colComplaint')}</th>
                </tr>
              </thead>
              <tbody>
                {alertRows.map((row) => (
                  <tr key={row.region} className="border-b last:border-0">
                    <td className="py-3 text-sm font-medium">{row.region.toUpperCase()}</td>
                    <td className="py-3 text-right text-sm">{row.alerts}</td>
                    <td className="py-3 text-right text-sm">{row.bounce.toFixed(2)}</td>
                    <td className="py-3 text-right text-sm">{row.complaint.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CostPanel({ period }: { period: DashboardPeriod }) {
  const { data, isLoading, error } = useEmailRegionalDashboard(period);
  const { t } = useI18n();

  const providerCpmUsd: Record<string, number> = {
    ses: 0.1,
    sendgrid: 0.95,
    mailgun: 0.8,
    postmark: 1.25,
  };

  const providerCosts = useMemo(() => {
    const totals = new Map<string, number>();

    for (const metric of data?.metrics ?? []) {
      for (const [provider, emails] of Object.entries(metric.provider_breakdown)) {
        totals.set(provider, (totals.get(provider) ?? 0) + emails);
      }
    }

    return Array.from(totals.entries())
      .map(([provider, emails]) => {
        const cpm = providerCpmUsd[provider.toLowerCase()] ?? 0.7;
        const estimatedCost = (emails / 1000) * cpm;

        return {
          provider,
          emails,
          cpm,
          estimatedCost,
        };
      })
      .sort((a, b) => b.estimatedCost - a.estimatedCost);
  }, [data?.metrics]);

  const totalEstimatedCost = providerCosts.reduce((sum, row) => sum + row.estimatedCost, 0);

  if (isLoading) {
    return <Skeleton className="h-48 w-full rounded-lg" />;
  }

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {t('admin.emailDashboard.cost.loadError')}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.emailDashboard.cost.title', { period })}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t('admin.emailDashboard.cost.totalCostPrefix')}{' '}
          {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(totalEstimatedCost)}
        </p>

        {providerCosts.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('admin.emailDashboard.cost.noData')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px]">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 font-medium">{t('admin.emailDashboard.cost.colProvider')}</th>
                  <th className="py-2 text-right font-medium">{t('admin.emailDashboard.cost.colEmails')}</th>
                  <th className="py-2 text-right font-medium">{t('admin.emailDashboard.cost.colCpm')}</th>
                  <th className="py-2 text-right font-medium">{t('admin.emailDashboard.cost.colEstimatedCost')}</th>
                </tr>
              </thead>
              <tbody>
                {providerCosts.map((row) => (
                  <tr key={row.provider} className="border-b last:border-0">
                    <td className="py-3 text-sm font-medium">{row.provider}</td>
                    <td className="py-3 text-right text-sm">{row.emails.toLocaleString()}</td>
                    <td className="py-3 text-right text-sm">{row.cpm.toFixed(2)}</td>
                    <td className="py-3 text-right text-sm">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(row.estimatedCost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CompliancePanel({
  period,
  thresholds,
  onThresholdChange,
}: {
  period: DashboardPeriod;
  thresholds: ComplianceThresholds;
  onThresholdChange: (patch: Partial<ComplianceThresholds>) => void;
}) {
  const { data, isLoading, error } = useEmailRegionalDashboard(period);
  const { t } = useI18n();

  const rows = useMemo(() => {
    return (data?.metrics ?? []).map((metric) => {
      let status: 'HEALTHY' | 'MONITOR' | 'DEGRADED' = 'DEGRADED';

      if (
        metric.bounce_rate <= thresholds.healthyBounceMax &&
        metric.complaint_rate <= thresholds.healthyComplaintMax
      ) {
        status = 'HEALTHY';
      } else if (
        metric.bounce_rate <= thresholds.monitorBounceMax &&
        metric.complaint_rate <= thresholds.monitorComplaintMax
      ) {
        status = 'MONITOR';
      }

      return {
        region: metric.region,
        bounce: metric.bounce_rate,
        complaint: metric.complaint_rate,
        status,
      };
    });
  }, [data?.metrics, thresholds]);

  if (isLoading) {
    return <Skeleton className="h-48 w-full rounded-lg" />;
  }

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {t('admin.emailDashboard.compliance.loadError')}
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.emailDashboard.compliance.title', { period })}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-muted-foreground">
            {t('admin.emailDashboard.compliance.healthyBounceMax')}
            <input
              className="mt-1 w-full rounded-md border bg-background px-2 py-1 text-sm"
              type="number"
              min={0}
              step={0.01}
              value={thresholds.healthyBounceMax}
              onChange={(event) =>
                onThresholdChange({ healthyBounceMax: Number(event.target.value) || 0 })
              }
            />
          </label>
          <label className="text-xs text-muted-foreground">
            {t('admin.emailDashboard.compliance.healthyComplaintMax')}
            <input
              className="mt-1 w-full rounded-md border bg-background px-2 py-1 text-sm"
              type="number"
              min={0}
              step={0.01}
              value={thresholds.healthyComplaintMax}
              onChange={(event) =>
                onThresholdChange({ healthyComplaintMax: Number(event.target.value) || 0 })
              }
            />
          </label>
          <label className="text-xs text-muted-foreground">
            {t('admin.emailDashboard.compliance.monitorBounceMax')}
            <input
              className="mt-1 w-full rounded-md border bg-background px-2 py-1 text-sm"
              type="number"
              min={0}
              step={0.01}
              value={thresholds.monitorBounceMax}
              onChange={(event) =>
                onThresholdChange({ monitorBounceMax: Number(event.target.value) || 0 })
              }
            />
          </label>
          <label className="text-xs text-muted-foreground">
            {t('admin.emailDashboard.compliance.monitorComplaintMax')}
            <input
              className="mt-1 w-full rounded-md border bg-background px-2 py-1 text-sm"
              type="number"
              min={0}
              step={0.01}
              value={thresholds.monitorComplaintMax}
              onChange={(event) =>
                onThresholdChange({ monitorComplaintMax: Number(event.target.value) || 0 })
              }
            />
          </label>
        </div>

        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('admin.emailDashboard.compliance.noData')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px]">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 font-medium">{t('admin.emailDashboard.compliance.colRegion')}</th>
                  <th className="py-2 text-right font-medium">{t('admin.emailDashboard.compliance.colBounce')}</th>
                  <th className="py-2 text-right font-medium">{t('admin.emailDashboard.compliance.colComplaint')}</th>
                  <th className="py-2 text-right font-medium">{t('admin.emailDashboard.compliance.colStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.region} className="border-b last:border-0">
                    <td className="py-3 text-sm font-medium">{row.region.toUpperCase()}</td>
                    <td className="py-3 text-right text-sm">{row.bounce.toFixed(2)}</td>
                    <td className="py-3 text-right text-sm">{row.complaint.toFixed(2)}</td>
                    <td className="py-3 text-right">
                      <Badge variant={statusVariant(row.status)}>{row.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function EmailDashboardPage() {
  const { currentTenant } = useTenant();
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState('overview');
  const [analyticsPeriod, setAnalyticsPeriod] = useState<DashboardPeriod>('7d');
  const [complianceThresholds, setComplianceThresholds] = useState<ComplianceThresholds>(
    DEFAULT_COMPLIANCE_THRESHOLDS,
  );

  const showSharedPeriodControl =
    activeTab === 'providers' ||
    activeTab === 'cost' ||
    activeTab === 'compliance' ||
    activeTab === 'alerts';

  useEffect(() => {
    const storageKey = `email-dashboard-compliance:${currentTenant?.id ?? 'global'}`;
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      setComplianceThresholds(DEFAULT_COMPLIANCE_THRESHOLDS);
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<ComplianceThresholds>;
      setComplianceThresholds({
        healthyBounceMax: parsed.healthyBounceMax ?? DEFAULT_COMPLIANCE_THRESHOLDS.healthyBounceMax,
        healthyComplaintMax:
          parsed.healthyComplaintMax ?? DEFAULT_COMPLIANCE_THRESHOLDS.healthyComplaintMax,
        monitorBounceMax: parsed.monitorBounceMax ?? DEFAULT_COMPLIANCE_THRESHOLDS.monitorBounceMax,
        monitorComplaintMax:
          parsed.monitorComplaintMax ?? DEFAULT_COMPLIANCE_THRESHOLDS.monitorComplaintMax,
      });
    } catch {
      setComplianceThresholds(DEFAULT_COMPLIANCE_THRESHOLDS);
    }
  }, [currentTenant?.id]);

  useEffect(() => {
    const storageKey = `email-dashboard-compliance:${currentTenant?.id ?? 'global'}`;
    localStorage.setItem(storageKey, JSON.stringify(complianceThresholds));
  }, [complianceThresholds, currentTenant?.id]);

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t('admin.emailDashboard.title')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('admin.emailDashboard.subtitle')}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <TabsList>
            <TabsTrigger value="overview">{t('admin.emailDashboard.tabs.overview')}</TabsTrigger>
            <TabsTrigger value="regional">{t('admin.emailDashboard.tabs.regional')}</TabsTrigger>
            <TabsTrigger value="providers">{t('admin.emailDashboard.tabs.providers')}</TabsTrigger>
            <TabsTrigger value="cost">{t('admin.emailDashboard.tabs.cost')}</TabsTrigger>
            <TabsTrigger value="compliance">{t('admin.emailDashboard.tabs.compliance')}</TabsTrigger>
            <TabsTrigger value="alerts">{t('admin.emailDashboard.tabs.alerts')}</TabsTrigger>
          </TabsList>

          {showSharedPeriodControl ? (
            <Select value={analyticsPeriod} onValueChange={(value: DashboardPeriod) => setAnalyticsPeriod(value)}>
              <SelectTrigger className="w-[110px]" aria-label={t('admin.emailDashboard.period.ariaLabel')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : null}
        </div>

        <TabsContent value="overview" className="space-y-6">
          <OverviewCards />
          <OverviewSummaryPanel />
        </TabsContent>

        <TabsContent value="regional">
          <EmailRegionalBreakdownPanel />
        </TabsContent>

        <TabsContent value="providers">
          <ProvidersPanel period={analyticsPeriod} />
        </TabsContent>

        <TabsContent value="cost">
          <CostPanel period={analyticsPeriod} />
        </TabsContent>

        <TabsContent value="compliance">
          <CompliancePanel
            period={analyticsPeriod}
            thresholds={complianceThresholds}
            onThresholdChange={(patch) =>
              setComplianceThresholds((current) => ({ ...current, ...patch }))
            }
          />
        </TabsContent>

        <TabsContent value="alerts">
          <AlertsPanel period={analyticsPeriod} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
